// API Configuration - Single source of truth for all API endpoints
// Update BACKEND_URL here when deploying to production

/**
 * Backend URL Configuration
 *
 * For Development:
 * - Local: http://localhost:5000 (or your local backend port)
 *
 * For Production:
 * - Replace with your production backend URL
 * - Example: https://api.bookatherapy.com
 */

// Read from environment variable, fallback to localhost for development
const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "https://api.bookatherapy.com";

/**
 * API Base URL
 * All API endpoints will be prefixed with this URL
 */
export const API_BASE_URL = `${BACKEND_URL}/api`;

/**
 * Backend URL (for non-API routes like health checks, etc.)
 */
export const BACKEND_BASE_URL = BACKEND_URL;

/**
 * API Endpoints Configuration
 * Organized by feature/module for easy maintenance
 */
export const API_ENDPOINTS = {
  // Authentication
  auth: {
    requestOtp: `${API_BASE_URL}/auth/client/request-otp`,
    verifyOtp: `${API_BASE_URL}/auth/client/verify-otp`,
  },

  // Therapists
  therapists: {
    countsBySetting: `${API_BASE_URL}/therapists/counts-by-setting`,
    getById: (id) => `${API_BASE_URL}/therapists/${id}`,
    search: `${API_BASE_URL}/therapists/search`,
  },

  // Matching & Availability
  match: {
    preview: `${API_BASE_URL}/match/preview`,
  },

  availability: {
    slots: `${API_BASE_URL}/availability/slots`,
  },

  // Bookings
  bookings: {
    hold: `${API_BASE_URL}/bookings/hold`,
    confirm: `${API_BASE_URL}/bookings/confirm`,
    list: `${API_BASE_URL}/bookings`,
    getById: (id) => `${API_BASE_URL}/bookings/${id}`,
  },

  // Gift Cards
  giftCards: {
    create: `${API_BASE_URL}/gift-cards/create`,
    paymentIntent: `${API_BASE_URL}/gift-cards/payment-intent`,
    verify: (code) => `${API_BASE_URL}/gift-cards/verify/${code}`,
    redeem: `${API_BASE_URL}/gift-cards/redeem`,
    getById: (id) => `${API_BASE_URL}/gift-cards/${id}`,
    myPurchases: `${API_BASE_URL}/gift-cards/my-purchases`,
  },

  // Massage Clinics
  massageClinics: {
    search: `${API_BASE_URL}/massage-clinics/search`,
  },

  // Loyalty Program
  loyalty: {
    program: `${API_BASE_URL}/loyalty/program`,
    verifyReferral: (code) => `${API_BASE_URL}/loyalty/verify-referral/${code}`,
    applyReferral: `${API_BASE_URL}/loyalty/apply-referral`,
    awardPoints: `${API_BASE_URL}/loyalty/award-points`,
    redeem: `${API_BASE_URL}/loyalty/redeem`,
    stats: `${API_BASE_URL}/loyalty/stats`,
    leaderboard: `${API_BASE_URL}/loyalty/leaderboard`,
  },

  // Contact Us
  contact: {
    submit: `${API_BASE_URL}/contact`,
    getAll: `${API_BASE_URL}/contact`,
    getById: (id) => `${API_BASE_URL}/contact/${id}`,
    updateStatus: (id) => `${API_BASE_URL}/contact/${id}/status`,
  },

  // SEO - Clinics, Cities & Therapists
  seo: {
    getClinic: (slug) => `${API_BASE_URL}/clinics/${slug}`,
    getCity: (slug) => `${API_BASE_URL}/cities/${slug}`,
    getTherapist: (slug) => `${API_BASE_URL}/therapists/profile/${slug}`,
    getSitemap: `${API_BASE_URL}/sitemap.xml`,
    createReview: `${API_BASE_URL}/reviews`,
    getGoogleBusinessProfile: (slug) =>
      `${API_BASE_URL}/clinics/${slug}/google-business-profile`,
  },

  // Centerfy.ai Tracking
  centerfy: {
    track: `${API_BASE_URL}/centerfy/track`,
    getConfig: `${API_BASE_URL}/centerfy/config`,
    getPageConfig: `${API_BASE_URL}/centerfy/pages`,
  },
};

/**
 * Helper function to build query string from params object
 */
export const buildQueryString = (params) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      searchParams.append(key, value);
    }
  });
  return searchParams.toString();
};

/**
 * Configuration for fetch requests
 */
export const DEFAULT_FETCH_CONFIG = {
  credentials: "include",
  headers: {
    "Content-Type": "application/json",
  },
};

// Export for backward compatibility
export const API = API_BASE_URL;

export default {
  API_BASE_URL,
  BACKEND_BASE_URL,
  API_ENDPOINTS,
  buildQueryString,
  DEFAULT_FETCH_CONFIG,
  API,
  BACKEND_URL,
};
